import React, {useState} from 'react';
import {Rate, Input, Button, Popconfirm, message, Tabs} from 'antd';
import type {TableColumnsType, TabsProps} from 'antd';
import {DeleteOutlined} from '@ant-design/icons';
import {addKeyData, formatDate} from 'utils';
import type {SearchProps} from 'antd/es/input/Search';
import Api from 'configs';

import {TableCustom} from 'components/element/TableCustom';
import {PaginationCustom} from 'components/element/PaginationCustom';
import {useGetReport} from 'hooks/report';
import {CustomerReviewsTab} from './CustomerReviewsTab';
import {ShopClosedReportsTab} from './ShopClosedReportsTab';

export const Reports: React.FC = () => {
  const [filters, setFilters] = useState({});
  const {data: getData, isLoading, refetch} = useGetReport(filters);

  const columns: TableColumnsType = [
    {
      title: 'Mã đơn hàng',
      dataIndex: 'order',
      key: 'code',
      align: 'center',
      width: 140,
      render: (data: any) => {
        return <div className="whitespace-nowrap font-medium text-blue-600">{data?.code || '—'}</div>;
      },
    },
    {
      title: 'Tên Người đánh giá',
      dataIndex: 'client',
      key: 'client',
      align: 'center',
      width: 170,
      render: (data: any) => {
        return <div className="whitespace-nowrap">{data?.name}</div>;
      },
    },
    {
      title: 'Tài xế',
      dataIndex: 'ship',
      key: 'ship',
      align: 'center',
      width: 170,
      render: (data: any) => {
        return <div className="whitespace-nowrap">{data?.name}</div>;
      },
    },
    {
      title: 'Nội dung',
      dataIndex: 'reason',
      key: 'reason',
      align: 'center',
      width: 250,
    },
    {
      title: 'Đánh giá',
      dataIndex: 'rating',
      key: 'rating',
      align: 'center',
      width: 170,
      render: (data: number) => {
        return <Rate defaultValue={data} disabled />;
      },
    },
    {
      title: 'Ngày báo cáo',
      dataIndex: 'createdAt',
      key: 'createdAt',
      align: 'center',
      width: 160,
      render: data => {
        return (
          <div className="whitespace-nowrap">
            <p>{formatDate(data)}</p>
          </div>
        );
      },
    },
    // F: Admin xóa đánh giá
    {
      title: 'Hành động',
      key: 'action',
      align: 'center',
      width: 110,
      render: (_: any, record: any) => (
        <Popconfirm
          title="Xóa đánh giá"
          description="Bạn có chắc muốn xóa đánh giá này?"
          okText="Xóa"
          cancelText="Hủy"
          okButtonProps={{danger: true}}
          onConfirm={async () => {
            try {
              await Api.delete(`/reviews/${record?._id}`);
              message.success('Đã xóa đánh giá');
              refetch();
            } catch {
              message.error('Lỗi khi xóa đánh giá');
            }
          }}>
          <Button danger size="small" icon={<DeleteOutlined />}>
            Xóa
          </Button>
        </Popconfirm>
      ),
    },
  ];

  const onSearchValue: SearchProps['onSearch'] = (value, _e, info) => {
    setFilters({...filters, search: value});
  };

  const driverReportsContent = (
    <div className="flex-col gap-4 mt-2">
      <div className="flex justify-between items-center mb-4">
        <div>
          <span className="text-gray-500 text-sm">
            Tổng số: <b>{getData?.totalCount || (getData as any)?.total || 0}</b> báo cáo / đánh giá
          </span>
        </div>
        <div>
          <Input.Search
            className="w-96"
            placeholder="Tìm kiếm theo tên tài xế hoặc mã đơn hàng"
            onSearch={onSearchValue}
            enterButton
            allowClear
          />
        </div>
      </div>
      <div className="flex flex-col items-end gap-3">
        <TableCustom
          isLoading={isLoading}
          columns={columns}
          data={addKeyData(getData?.result)}
          scroll={{x: 1180, y: 590}}
        />
        <PaginationCustom
          totalPage={getData?.totalPage ?? 1}
          current={getData?.page ?? 1}
          pageSize={getData?.pageSize ?? 10}
          onChange={(page, pageSize) => {
            setFilters({...filters, page, pageSize});
          }}
          onShowSizeChange={(current, size) => {
            setFilters({...filters, page: current, pageSize: size});
          }}
        />
      </div>
    </div>
  );

  const tabItems: TabsProps['items'] = [
    {
      key: '1',
      label: 'Khách đánh giá tài xế & Báo cáo',
      children: driverReportsContent,
    },
    {
      key: '2',
      label: '⭐ Shipper đánh giá khách hàng',
      children: <CustomerReviewsTab />,
    },
    {
      key: '3',
      label: '🏪 Lịch sử báo quán đóng cửa',
      children: <ShopClosedReportsTab />,
    },
  ];

  return (
    <div className="container mx-auto">
      <div className="flex-col gap-4">
        <div className="mb-2">
          <h2 className="text-2xl font-bold">Quản lý Báo cáo & Đánh giá</h2>
        </div>
        <Tabs defaultActiveKey="1" items={tabItems} size="large" />
      </div>
    </div>
  );
};
